// src/pages/HomePage.js
import { Grid, CircularProgress } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../contexts/RoleContext";

import Card from "../components/ui/Card";
import SystemInfo from "../components/HomePage/SystemInfo";
import { useProfileData } from "../hooks/useProfileData";
import { getProfileCards } from "../utils/profileHelpers";
import { useDonorDashboardData } from "../hooks/useDonorDashboardData";
import { useReceiverDashboardStats } from "../hooks/useReceiverDashboardStats"; // ✅ NEW
import { useNavigate } from "react-router-dom";
import { getTimeAgo } from "../utils/dateUtils";

import "../style/HomePage.css";

// Demo mode toggle - set to true to show fake counts, false for real data
// NOTE: Kept for DONOR section as requested. Receiver section no longer uses this.
const DEMO_NEW_REQUESTS = true;

function Homepage() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentRole } = useRole();
  const navigate = useNavigate();

  const roleLower = String(currentRole || "").toLowerCase();
  const isDonorSection = roleLower.includes("donor");
  const isReceiverSection = roleLower.includes("receiver");

  const [isVisible, setIsVisible] = useState(false);

  const { profile, loading } = useProfileData();
  const { stats: donorStats } = useDonorDashboardData();

  /* ===================== RECEIVER STATS (DERIVED LIKE SCHEDULE) ===================== */
  const {
    loading: receiverStatsLoading,
    error: receiverStatsError,
    stats: receiverStats,
  } = useReceiverDashboardStats(user);

  /* ===================== FADE-IN ===================== */
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  /* ===================== COUNTS ===================== */
  const donorNewRequestsCount = DEMO_NEW_REQUESTS
    ? 3
    : donorStats?.new_requests_since_login || 0;

  // Receiver now uses derived stats (no demo)
  const receiverNewDeliveriesCount =
    receiverStats?.newDeliveriesSinceLogin || 0;

  /* ===================== LOADING ===================== */
  if (loading) {
    return (
      <div
        className={`homepage-loading ${darkMode ? "dark-mode" : "light-mode"}`}
      >
        <CircularProgress className="spinner" />
      </div>
    );
  }

  const profileCards = getProfileCards(profile, t);

  return (
    <div
      className={`homepage-container ${
        darkMode ? "dark-mode" : "light-mode"
      } ${roleLower}-role ${isVisible ? "visible" : ""}`}
    >
      <div className="homepage-bg-decoration">
        <div className="bg-circle bg-circle-1"></div>
        <div className="bg-circle bg-circle-2"></div>
        <div className="bg-mesh"></div>
      </div>

      {/* ===================== HERO (smaller) ===================== */}
      <div className="homepage-hero">
        <div className="homepage-hero-content">
          <div className="welcome-badge">
            <span className="welcome-badge-icon">👋</span>
            <span className="welcome-badge-text">{t("Welcome Back")}</span>
          </div>

          <h1 className="homepage-title">
            {user?.company_name || user?.username || "User"}
          </h1>

          <p className="homepage-subtitle">
            {t("Your sustainability impact dashboard")}
          </p>
        </div>

        <div className="hero-accent-shape"></div>
      </div>

      {/* ===================== DONOR METRICS ===================== */}
      {isDonorSection && (
        <div className="metrics-section">
          {/* Notification ABOVE counters */}
          <div
            className={`notification-card clickable ${
              !donorStats || donorNewRequestsCount === 0 ? "no-new" : ""
            }`}
            style={{ animationDelay: "0.05s" }}
            onClick={() => navigate("/donor/allocate")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/donor/allocate")}
          >
            <div className="notification-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>

            <div className="notification-content">
              {!donorStats ? (
                <span className="skeleton-text skeleton-notification"></span>
              ) : donorNewRequestsCount > 0 ? (
                <>
                  <span className="notification-count">
                    {donorNewRequestsCount}
                  </span>
                  <span className="notification-text">
                    {t("new request(s) since your last login")}
                  </span>
                </>
              ) : (
                <span className="notification-text">
                  {t("No new requests since your last visit")}
                </span>
              )}
            </div>

            <div className="notification-action">
              <span>{t("View your requests")}</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.1s" }}>
                <div className="metric-icon listings-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="1" />
                    <path d="M9 12h6" />
                    <path d="M9 16h6" />
                  </svg>
                </div>
                <div className="metric-content">
                  {!donorStats ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value">
                        {donorStats.total_listings}
                      </div>
                      <div className="metric-label">{t("Active Listings")}</div>
                      <div className="metric-description">
                        {t("Materials available")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.2s" }}>
                <div className="metric-icon pending-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="metric-content">
                  {!donorStats ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value">
                        {donorStats.pending_requests}
                      </div>
                      <div className="metric-label">
                        {t("Pending Requests")}
                      </div>
                      <div className="metric-description">
                        {t("Awaiting your action")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.3s" }}>
                <div className="metric-icon expiring-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="metric-content">
                  {!donorStats ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value">
                        {donorStats.expiring_soon}
                      </div>
                      <div className="metric-label">{t("Expiring Soon")}</div>
                      <div className="metric-description">
                        {t("Within 2 weeks")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.4s" }}>
                <div className="metric-icon last-request-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div className="metric-content">
                  {!donorStats ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value metric-value-small">
                        {donorStats.last_request_time
                          ? getTimeAgo(donorStats.last_request_time)
                          : t("None")}
                      </div>
                      <div className="metric-label">{t("Last Request")}</div>
                      <div className="metric-description">
                        {t("Most recent incoming")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      )}

      {/* ===================== RECEIVER METRICS (UPDATED) ===================== */}
      {isReceiverSection && (
        <div className="metrics-section">
          {/* Notification ABOVE counters */}
          <div
            className={`notification-card clickable ${
              receiverStatsLoading || receiverNewDeliveriesCount === 0
                ? "no-new"
                : ""
            }`}
            style={{ animationDelay: "0.05s" }}
            onClick={() => navigate("/receiver/schedule")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && navigate("/receiver/schedule")
            }
          >
            <div className="notification-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>

            <div className="notification-content">
              {receiverStatsLoading ? (
                <span className="skeleton-text skeleton-notification"></span>
              ) : receiverNewDeliveriesCount > 0 ? (
                <>
                  <span className="notification-count">
                    {receiverNewDeliveriesCount}
                  </span>
                  <span className="notification-text">
                    {t("new delivery(ies) since your last login")}
                  </span>
                </>
              ) : (
                <span className="notification-text">
                  {t("No new deliveries since your last visit")}
                </span>
              )}
            </div>

            <div className="notification-action">
              <span>{t("View your deliveries")}</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Error hint */}
          {receiverStatsError ? (
            <div style={{ marginTop: 12 }}>
              <div className="notification-card no-new">
                <div className="notification-content">
                  <span className="notification-text">
                    Receiver metrics failed to load. Check console for details.
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.1s" }}>
                <div className="metric-icon listings-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="1" />
                    <path d="M9 12h6" />
                    <path d="M9 16h6" />
                  </svg>
                </div>
                <div className="metric-content">
                  {receiverStatsLoading ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value">
                        {receiverStats?.totalReceived ?? 0}
                      </div>
                      <div className="metric-label">{t("Total Received")}</div>
                      <div className="metric-description">
                        {t("All-time deliveries")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.2s" }}>
                <div className="metric-icon pending-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="metric-content">
                  {receiverStatsLoading ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value">
                        {receiverStats?.pendingPickups ?? 0}
                      </div>
                      <div className="metric-label">{t("Pending Pickups")}</div>
                      <div className="metric-description">
                        {t("Scheduled / awaiting")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.3s" }}>
                <div className="metric-icon expiring-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="metric-content">
                  {receiverStatsLoading ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value">
                        {receiverStats?.received30d ?? 0}
                      </div>
                      <div className="metric-label">{t("Received (30d)")}</div>
                      <div className="metric-description">
                        {t("Recent volume")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className="metric-card" style={{ animationDelay: "0.4s" }}>
                <div className="metric-icon last-request-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div className="metric-content">
                  {receiverStatsLoading ? (
                    <>
                      <div className="skeleton-text skeleton-value"></div>
                      <div className="skeleton-text skeleton-label"></div>
                      <div className="skeleton-text skeleton-desc"></div>
                    </>
                  ) : (
                    <div className="metric-data-loaded">
                      <div className="metric-value metric-value-small">
                        {receiverStats?.lastDeliveryTime
                          ? getTimeAgo(receiverStats.lastDeliveryTime)
                          : t("None")}
                      </div>
                      <div className="metric-label">{t("Last Delivery")}</div>
                      <div className="metric-description">
                        {t("Most recent received")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      )}

      <div className="section-divider">
        <div className="divider-line"></div>
        <div className="divider-label">{t("Company Overview")}</div>
        <div className="divider-line"></div>
      </div>

      <div className="profile-grid">
        <Grid container spacing={3}>
          {profileCards.map((card, index) => (
            <Grid
              item
              xs={12}
              md={card.fullWidth ? 12 : 6}
              key={index}
              style={{ animationDelay: `${0.3 + index * 0.1}s` }}
              className="profile-card-wrapper"
            >
              <Card
                icon={card.icon}
                title={card.label}
                value={card.value}
                variant="info"
                darkMode={darkMode}
              />
            </Grid>
          ))}
        </Grid>
      </div>

      <div className="system-info-wrapper" style={{ animationDelay: "1s" }}>
        <SystemInfo profile={profile} darkMode={darkMode} t={t} />
      </div>

      <div className="homepage-footer-accent"></div>
    </div>
  );
}

export default Homepage;